/* eslint-disable no-extra-boolean-cast */
import moment from 'moment';
import { EmploymentIncomeExpenseModel, EmploymentModel, IncomeModel } from '../../cmg-1003/models';
import { EmploymentStatus } from '../../cmg-1003/models/lookups/employment.lookups';
import { isNullish } from '../../shared/utils';

export class EmploymentIncomeExpensesUtils {
  /**
   * Calculations taken from Clear 1 and cleaned up a little bit.
   * Not sure if this is the right place - parking it temporarily here
   * till I find the right place for it.
   * TODO: check for nulls, correctness and call this
   */
  static calculateNoOfYearsAndMonths(fromDate: Date | string, toDate: Date | string, employmentType: EmploymentStatus) {
    const yearsMonths: { noOfYears: number; noOfMonths: number } = { noOfYears: 0, noOfMonths: 0 }; // if the data is not filled in properly
    if (employmentType === EmploymentStatus.SecondaryCurrent || employmentType === EmploymentStatus.Primary) {
      if (!!fromDate) {
        const startDate = moment(fromDate);
        const endDate = moment(new Date()); // present date
        const diffDuration = moment.duration(endDate.diff(startDate));
        yearsMonths.noOfYears = diffDuration.years();
        yearsMonths.noOfMonths = diffDuration.months();
      }
    } else if (employmentType === EmploymentStatus.Former) {
      if (!!toDate && !!fromDate) {
        const startDate = moment(fromDate);
        const endDate = moment(toDate);
        const diffDuration = moment.duration(endDate.diff(startDate));
        yearsMonths.noOfYears = diffDuration.years();
        // Clear 1 rounds the months
        yearsMonths.noOfMonths = diffDuration.days() > 0 ? diffDuration.months() + 1 : diffDuration.months();
        if (yearsMonths.noOfMonths > 11) {
          yearsMonths.noOfMonths -= 12;
          yearsMonths.noOfYears++;
        }
      }
    }
    return yearsMonths;
  }

  /**
   * Checks to see if the employment model has required values filled in which means
   * it is not one of the default model created by byte.
   * Based on current understanding
   * 1) employmentId will have a positive value(b/c byte already created it)
   * 2) Byte sets employment Type to Primary
   * 3) Byte leaves employerName empty
   * - add more if needed.
   * Once the employername( required field ) is filled then we are not marking it as default anymore.
   * @param empModel
   */
  static isDefaultByteEmployment(empModel: EmploymentModel): boolean {
    return empModel.employmentType === EmploymentStatus.Primary && empModel.employmentId > 0 && !empModel.employerName;
  }

  /**
   * Filters gross monthly incomes to include only valid incomes from current employments.
   * Excludes incomes from former employments and invalid income records.
   * @param empIncExp The employment income expense model containing employers and incomes
   * @param borrowerId The borrower ID to filter incomes for
   * * @returns Array of valid income models from current employments only
   */
  static getValidCurrentEmploymentIncomes(empIncExp: EmploymentIncomeExpenseModel, borrowerId?: number): IncomeModel[] {
    if (!empIncExp?.grossMonthlyIncomes) {
      return [];
    }

    // Create a map of employmentId -> employmentType to filter out former employments
    const allEmployers = [...(empIncExp.borrowerEmployers || []), ...(empIncExp.coBorrowerEmployers || [])];
    const employmentTypeMap = new Map(allEmployers.map((employer) => [employer.employmentId, employer.employmentType]));

    return empIncExp.grossMonthlyIncomes.filter((income) => {
      // Borrower ID filter (if specified)
      if (borrowerId !== undefined && income.borrowerId !== borrowerId) {
        return false;
      }

      // Basic validation
      if (income.isDeleted === true || !income?.amount) {
        return false;
      }

      // Income type validation (always required)
      if (!income.incomeType) {
        return false;
      }

      // Filter out income from former employments (always excluded)
      if (income.employmentId) {
        const employmentType = employmentTypeMap.get(income.employmentId);
        if (isNullish(employmentType) || employmentType === EmploymentStatus.Former) {
          return false;
        }
      }

      return true;
    });
  }

  /***
   * Calculates the Total Gross Monthly Income amount across all applications
   * Excludes incomes from former employments - only includes current employment income
   */
  static getTotalAllIncomes(incomes: EmploymentIncomeExpenseModel[]): number {
    let total = 0;
    if (incomes && incomes?.length > 0) {
      incomes?.forEach((empIncExp) => {
        const validIncomes = this.getValidCurrentEmploymentIncomes(empIncExp); // No borrowerId filter - includes all borrowers
        total += validIncomes.reduce((sum, income) => sum + income.amount, 0);
      });
    }
    return total;
  }
}
